/**
 * Two follow-up lists for the comms team, above the leads table:
 *
 *  - Pending queue: leads still waiting on a Yes/No, oldest first, with an
 *    age badge (amber from 10 days, red from 14: Art's two weeks, which is
 *    also how long Core will write a mark).
 *  - Missed calls: qualified calls and voicemails nobody followed up on.
 *
 * Read-only: each row opens the lead drawer, where the team corrects it.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  getMissedCalls,
  getPendingQueue,
  type WcFollowUpLead,
} from '@/services/whatconvertsService';

import { ageText, leadTitle, pendingLine, SEVERITY_BADGE } from './followUp';
import { labelerName } from './leadLabels';

type View = 'pending' | 'missed';

function Row({ lead, onOpen, line }: { lead: WcFollowUpLead; onOpen: (lead: WcFollowUpLead) => void; line: string }) {
  return (
    <li
      className="flex items-start justify-between gap-3 border-b border-border/60 py-2 last:border-0"
      data-testid={`followup-row-${lead.lead_id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium">{leadTitle(lead)}</span>
          <Badge
            variant="outline"
            className={SEVERITY_BADGE[lead.severity]}
            data-testid={`followup-age-${lead.lead_id}`}
            data-severity={lead.severity}
          >
            {ageText(lead.age_days)}
          </Badge>
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {line}
          {lead.labeler ? ` (${labelerName(lead.labeler)})` : ''}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={() => onOpen(lead)}>
        Open
      </Button>
    </li>
  );
}

export function FollowUpPanel({
  tenantId,
  onOpenLead,
}: {
  tenantId: string;
  onOpenLead: (lead: WcFollowUpLead) => void;
}) {
  const [view, setView] = useState<View>('pending');
  const pending = useQuery({
    queryKey: ['wc-pending-queue', tenantId],
    queryFn: () => getPendingQueue(tenantId),
    retry: 0,
    refetchOnWindowFocus: false,
  });
  const missed = useQuery({
    queryKey: ['wc-missed-calls', tenantId],
    queryFn: () => getMissedCalls(tenantId),
    retry: 0,
    refetchOnWindowFocus: false,
  });

  const p = pending.data;
  const m = missed.data;
  if (pending.isError && missed.isError) return null; // labels not set up: say nothing extra
  const active = view === 'pending' ? pending : missed;

  return (
    <Card data-testid="followup-panel">
      <CardContent className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            Follow-up
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant={view === 'pending' ? 'default' : 'outline'}
              onClick={() => setView('pending')}
              aria-pressed={view === 'pending'}
            >
              Pending{p ? ` (${p.summary.total})` : ''}
            </Button>
            <Button
              size="sm"
              variant={view === 'missed' ? 'default' : 'outline'}
              onClick={() => setView('missed')}
              aria-pressed={view === 'missed'}
            >
              Missed calls{m ? ` (${m.summary.count})` : ''}
            </Button>
          </div>
        </div>

        {view === 'pending' && p ? (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="followup-summary">
            {p.summary.total === 0
              ? 'Nothing is waiting on a decision.'
              : `${p.summary.total} waiting, oldest ${ageText(p.summary.oldest_age_days)}. ` +
                `${p.summary.amber} from ${p.thresholds.amber_days} days, ` +
                `${p.summary.red} from ${p.thresholds.red_days} days.`}
          </p>
        ) : null}
        {view === 'missed' && m ? (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="followup-summary">
            {m.summary.count === 0
              ? 'No qualified missed calls or voicemails are waiting on a follow-up.'
              : `${m.summary.count} qualified missed calls or voicemails without follow-up, oldest ${ageText(
                  m.summary.oldest_age_days,
                )}. ${m.definition.text}`}
          </p>
        ) : null}

        {active.isLoading ? (
          <p className="mt-2 text-xs text-muted-foreground">Loading…</p>
        ) : active.isError ? (
          <p className="mt-2 text-xs text-muted-foreground">This list could not be loaded.</p>
        ) : (
          <ul className="mt-2 max-h-72 overflow-y-auto" data-testid="followup-list">
            {(view === 'pending' ? p?.leads : m?.leads)?.map((lead) => (
              <Row
                key={lead.lead_id}
                lead={lead}
                onOpen={onOpenLead}
                line={
                  view === 'pending'
                    ? pendingLine(lead)
                    : `${lead.voicemail ? 'Voicemail' : 'Call nobody answered'}. ${lead.reason ?? ''}`.trim()
                }
              />
            ))}
          </ul>
        )}
        {active.data?.summary.truncated ? (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="followup-truncated">
            There are more leads than fit in this list. It shows the oldest ones; the newest are left out.
          </p>
        ) : null}
        <p className="mt-2 text-xs text-muted-foreground">
          Lists update with the lead sync, so a lead someone just marked in WhatConverts can take a
          little while to leave.
        </p>
      </CardContent>
    </Card>
  );
}
